import 'isomorphic-fetch';
import { startAction, successAction, failureAction, asyncAction } from './actionUtils';
import { normalizeCalendarDay } from './schemas';

export const toJson = (res) => res.json();

export const checkStatus = (res) => {
  const { status } = res;
  if (status >= 200 && status < 300) {
    return res;
  }

  return Promise.reject(new Error(res.statusText || res.status));
};

export const fetchJson = (url, options = {}) => (
  fetch(url, {
    ...options,    
    credentials: 'include',
    headers: {
      ...options.headers,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  })
  .then(checkStatus)
  .then(toJson)
);

const fetchSessionType = 'app/fetchSession';
export const fetchSessionStart = startAction(fetchSessionType);
export const fetchSessionSuccess = successAction(fetchSessionType);
export const fetchSessionFailure = failureAction(fetchSessionType);
export const fetchSession = asyncAction({
  func: () => {
    return fetchJson('/api/sessions/current')
  },
  start: fetchSessionStart,
  success: fetchSessionSuccess,
  failure: fetchSessionFailure,
});

const createSessionType = 'app/createSession';
export const createSessionStart = startAction(createSessionType);
export const createSessionSuccess = (data, dispatch) => {
  dispatch(fetchSession());
  return successAction(createSessionType);
}
export const createSessionFailure = failureAction(createSessionType);
export const createSession = asyncAction({
  func: (content) => {
    return fetchJson(
      '/api/sessions',
      {
        method: 'POST',
        body: JSON.stringify({ session: content })
      });
  },
  start: createSessionStart,
  success: createSessionSuccess,
  failure: createSessionFailure,
});

const fetchTabsType = 'app/fetchTabs';
export const fetchTabsStart = startAction(fetchTabsType);
export const fetchTabsSuccess = successAction(fetchTabsType);
export const fetchTabsFailure = failureAction(fetchTabsType);
export const fetchTabs = asyncAction({
  func: () => {
    return fetchJson('/api/visitors/schema')
  },
  start: fetchTabsStart,
  success: fetchTabsSuccess,
  failure: fetchTabsFailure,
});

const incrementEntryType = 'app/incrementEntry';
export const incrementEntryStart = startAction(incrementEntryType);
export const incrementEntrySuccess = successAction(incrementEntryType);
export const incrementEntryFailure = failureAction(incrementEntryType);
export const incrementEntry = asyncAction({
  func: (day, tag) => {
    return fetchJson(
      '/api/visitors/increment',
      {
        method: "POST",
        body: JSON.stringify({counter: { day: day, visitType: tag }})
      });
  },
  start: incrementEntryStart,
  success: incrementEntrySuccess,
  failure: incrementEntryFailure,
});

const decrementEntryType = 'app/decrementEntry';
export const decrementEntryStart = startAction(decrementEntryType);
export const decrementEntrySuccess = successAction(decrementEntryType);
export const decrementEntryFailure = failureAction(decrementEntryType);
export const decrementEntry = asyncAction({
  func: (day, tag) => {
    return fetchJson(
      '/api/visitors/decrement',
      {
        method: "POST",
        body: JSON.stringify({ counter: { day: day, visitType: tag }})
      });
  },
  start: decrementEntryStart,
  success: decrementEntrySuccess,
  failure: decrementEntryFailure,
});

const fetchCountsType = 'app/fetchCounts';
export const fetchCountsStart = startAction(fetchCountsType);
export const fetchCountsSuccess = successAction(fetchCountsType);
export const fetchCountsFailure = failureAction(fetchCountsType);
export const fetchCounts = asyncAction({
  func: (day) => {
    return fetchJson(`/api/visitors/counts/${day}`);
  },
  start: fetchCountsStart,
  success: fetchCountsSuccess,
  failure: fetchCountsFailure,
});

const searchPatientType = 'app/searchPatient';
export const searchPatientStart = startAction(searchPatientType);
export const searchPatientSuccess = successAction(searchPatientType);
export const searchPatientFailure = failureAction(searchPatientType);
export const searchPatient = asyncAction({
  func: (name) => {
    return fetchJson(
      `/api/prescriptions/patients/search`,
      {
        method: "POST",
        body: JSON.stringify({ term: name })
      });
  },
  start: searchPatientStart,
  success: searchPatientSuccess,
  failure: searchPatientFailure,
});

const fetchPatientType = 'app/fetchPatient';
export const fetchPatientStart = startAction(fetchPatientType);
export const fetchPatientSuccess = successAction(fetchPatientType);
export const fetchPatientFailure = failureAction(fetchPatientType);
export const fetchPatient = asyncAction({
  func: (id) => {
    return fetchJson(`/api/prescriptions/patients/${id}`)
  },
  start: fetchPatientStart,
  success: fetchPatientSuccess,
  failure: fetchPatientFailure,
});


const fetchCalendarDayType = 'app/fetchCalendarDay';
export const fetchCalendarDayStart = startAction(fetchCalendarDayType);
export const fetchCalendarDaySuccess = successAction(fetchCalendarDayType);
export const fetchCalendarDayFailure = failureAction(fetchCalendarDayType);
export const fetchCalendarDay = asyncAction({
  func: (day) => {
    return (fetchJson(`/api/calendar/chunks/${day}`)
                .then(normalizeCalendarDay));
  },
  start: fetchCalendarDayStart,
  success: fetchCalendarDaySuccess,
  failure: fetchCalendarDayFailure,
});



const updateAppointmentType = 'app/updateAppointment';
export const updateAppointmentStart = startAction(updateAppointmentType);
export const updateAppointmentSuccess = successAction(updateAppointmentType);
export const updateAppointmentFailure = failureAction(updateAppointmentType);
export const updateAppointment = asyncAction({
  func: (appointment) => {
    return fetchJson(
      `/api/calendar/appointments/${appointment.id}`,
      { method: "PUT",
        body: JSON.stringify({ appointment })
      });
  },
  start: updateAppointmentStart,
  success: updateAppointmentSuccess,
  failure: updateAppointmentFailure,
});

const addShiftType = 'app/addShift';
export const addShiftStart = startAction(addShiftType);
export const addShiftSuccess = successAction(addShiftType);
export const addShiftFailure = failureAction(addShiftType);
export const addShift = asyncAction({
  func: (chunk) => {
    return fetchJson(
      `/api/calendar/chunks`,
      { method: "POST",
        body: JSON.stringify({ chunk })
      });
  },
  start: addShiftStart,
  success: addShiftSuccess,
  failure: addShiftFailure,
});

const addPatientType = 'app/addPatient';
export const addPatientStart = startAction(addPatientType);
export const addPatientSuccess = successAction(addPatientType);
export const addPatientFailure = failureAction(addPatientType);
export const addPatient = asyncAction({
  func: (patient) => {
    return fetchJson(
      `/api/prescriptions/patients`,
      { method: "POST",
        body: JSON.stringify({ patient })
      });
  },
  start: addPatientStart,
  success: addPatientSuccess,
  failure: addPatientFailure,
});

const updatePatientType = 'app/updatePatient';
export const updatePatientStart = startAction(updatePatientType);
export const updatePatientSuccess = successAction(updatePatientType);
export const updatePatientFailure = failureAction(updatePatientType);
export const updatePatient = asyncAction({
  func: (patient) => {
    return fetchJson(
      `/api/prescriptions/patients/${patient.id}`,
      { method: "PUT",
        body: JSON.stringify({ patient })
      });
  },
  start: updatePatientStart,
  success: updatePatientSuccess,
  failure: updatePatientFailure,
});

const addPrescriptionType = 'app/addPrescription';
export const addPrescriptionStart = startAction(addPrescriptionType);
export const addPrescriptionSuccess = successAction(addPrescriptionType);
export const addPrescriptionFailure = failureAction(addPrescriptionType);
export const addPrescription = asyncAction({
  func: (patient, prescription) => {
    return fetchJson(
      `/api/prescriptions/patients/${patient.id}/pickups`,
      { method: "POST",
        body: JSON.stringify({ prescription_pickup: prescription })
      });
  },
  start: addPrescriptionStart,
  success: addPrescriptionSuccess,
  failure: addPrescriptionFailure,
});




// WEBPACK FOOTER //
// ./src/store/actions.js